import { Badge, Pagination, Select } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { ReactNode, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatSharePercent, getSharePercent } from './reportShare';

type VehicleReportTarget = {
    vehicleLabel: string;
    fieldValues: Record<string, string>;
};

export const getSearchLabel = (vehicle: VehicleReportTarget) => vehicle.vehicleLabel?.trim()
    || Object.values(vehicle.fieldValues ?? {}).filter(Boolean).join(' ')
    || 'Unknown vehicle';

const normalizedFields = (vehicle: VehicleReportTarget) => Object.entries(vehicle.fieldValues ?? {})
    .reduce<Record<string, string>>((fields, [key, value]) => {
        if (key.trim() && value?.trim()) fields[key.trim().toLowerCase()] = value.trim();
        return fields;
    }, {});

const fieldLabel = (key: string) => key.replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase()).replace(/\bCc\b/g, 'CC');

const VehicleIdentity = ({ vehicle, rank }: { vehicle: VehicleReportTarget; rank: number }) => {
    const fields = normalizedFields(vehicle);
    const label = getSearchLabel(vehicle);
    const titleFields = ['make', 'model', 'submodel'];
    const hasIdentity = Boolean(fields.make || fields.model);
    const title = hasIdentity ? titleFields.map(key => fields[key]).filter(Boolean).join(' ') : label;
    const details = Object.entries(fields)
        .filter(([key]) => !hasIdentity || !titleFields.includes(key))
        .sort(([left], [right]) => Number(right === 'year') - Number(left === 'year') || left.localeCompare(right));
    const params = new URLSearchParams();
    params.set('fieldValues', JSON.stringify(fields));
    ['year', ...titleFields].forEach(key => { if (fields[key]) params.set(key, fields[key]); });

    return (
        <div className="pf-report-vehicle">
            <span className="pf-report-rank" aria-label={`Rank ${rank}`}>{rank}</span>
            <div className="pf-report-vehicle-identity">
                {Object.keys(fields).length ? (
                    <Link to={`/products?${params.toString()}`} className="pf-report-vehicle-link"
                        aria-label={`View products for ${label}`} title="View this vehicle in Products">
                        {title}
                    </Link>
                ) : <strong>{title}</strong>}
                {details.length > 0 && (
                    <div className="pf-report-vehicle-meta">
                        {details.map(([key, value]) => (
                            <span key={key} title={`${fieldLabel(key)}: ${value}`}>
                                {key === 'year' ? value : `${fieldLabel(key)}: ${value}`}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export type VehicleReportRow = VehicleReportTarget & {
    currentMappedProductCount: number;
    searchCount?: number;
    previousSearchCount?: number;
    lastSearchedOn?: string;
};

type DetailMode = 'searches' | 'opportunities' | 'health';
type Props = {
    title: string;
    description: string;
    mode: DetailMode;
    rows: VehicleReportRow[];
    totalSearches?: number;
    comparison?: boolean;
    comparisonComplete?: boolean;
    notice?: ReactNode;
    controls?: ReactNode;
    emptyMessage: ReactNode;
};

const count = (value = 0) => value.toLocaleString();
const lastSearched = (value?: string) => {
    const date = new Date(`${value?.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};

export default function VehicleReportDetails({ title, description, mode, rows, comparison = false,
    comparisonComplete = true, totalSearches, notice, controls, emptyMessage }: Props) {
    const [expanded, setExpanded] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const id = useId();
    const Chevron = expanded ? ChevronUpIcon : ChevronDownIcon;
    const coverage = mode === 'health' || mode === 'opportunities';
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const columns: { label: string; numeric?: boolean; value: (row: VehicleReportRow) => ReactNode }[] = [];

    if (mode !== 'health') columns.push({ label: 'Searches / share', numeric: true, value: row => {
        const share = getSharePercent(row.searchCount ?? 0, totalSearches ?? 0);
        return <div className="pf-report-search-share">
            <span className="pf-report-search-share__values"><strong>{count(row.searchCount)}</strong>
                <span title={share === null ? 'Share unavailable: no search total' : 'Share of all recorded searches in the selected period'}>
                    {formatSharePercent(share)}
                </span>
            </span>
            {share !== null && <span className="pf-report-search-share__track" aria-hidden="true"><span style={{ width: `${share}%` }} /></span>}
        </div>;
    } });
    if (coverage) {
        columns.push({ label: 'Mapped products', numeric: true, value: row => count(row.currentMappedProductCount) });
        columns.push({ label: 'Coverage', value: row => row.currentMappedProductCount === 0
            ? <Badge tone="critical">No mappings</Badge> : <Badge tone="warning">Low coverage</Badge> });
    } else if (comparison) {
        columns.push({ label: comparisonComplete ? 'Comparison' : 'Comparison (partial)', numeric: true,
            value: row => count(row.previousSearchCount) });
        columns.push({ label: 'Change in searches', numeric: true, value: row => {
            if (!comparisonComplete) return <span title="Change withheld because comparison data is incomplete">—</span>;
            const change = (row.searchCount ?? 0) - (row.previousSearchCount ?? 0);
            return (
                <span className="pf-report-search-change">
                    {change > 0 ? '+' : ''}{count(change)}
                    {row.previousSearchCount === 0 && (row.searchCount ?? 0) > 0 && (
                        <span className="pf-report-new" title="Searched in this period, with no searches in the comparison period">New</span>
                    )}
                </span>
            );
        } });
    }
    if (mode !== 'health') columns.push({ label: 'Last searched', value: row => lastSearched(row.lastSearchedOn) });

    return (
        <section className="pf-report-details" aria-labelledby={`${id}-heading`}>
            <div className="pf-report-details-heading">
                <h3 id={`${id}-heading`}>{title}<span className="pf-report-details-count">{count(rows.length)} {rows.length === 1 ? 'vehicle' : 'vehicles'}</span></h3>
                <button type="button" className="pf-report-details-toggle" aria-expanded={expanded}
                    aria-controls={`${id}-body`} onClick={() => setExpanded(value => !value)}>
                    {expanded ? 'Hide details' : 'Show details'}<Chevron width={20} height={20} aria-hidden="true" focusable="false" />
                </button>
            </div>
            {expanded && (
                <div id={`${id}-body`}>
                    <p className="pf-report-details-description">{description}</p>
                    {mode !== 'health' && <p className="pf-report-share-context">Shares use all {count(totalSearches)} recorded searches in the selected period.</p>}
                    {controls}
                    {notice}
                    {rows.length === 0 ? <div className="pf-report-empty">{emptyMessage}</div> : (
                        <>
                            <table className="pf-report-results-table" aria-label={title} role="table">
                                <thead><tr role="row">
                                    <th scope="col" role="columnheader">Vehicle</th>
                                    {columns.map(column => <th key={column.label} scope="col" role="columnheader"
                                        className={column.numeric ? 'pf-report-numeric' : undefined}>{column.label}</th>)}
                                </tr></thead>
                                <tbody>{rows.slice(start, start + pageSize).map((row, index) => (
                                    <tr key={JSON.stringify([row.fieldValues, row.vehicleLabel, start + index])} role="row">
                                        <td className="pf-report-vehicle-cell" role="cell"><VehicleIdentity vehicle={row} rank={start + index + 1} /></td>
                                        {columns.map(column => <td key={column.label} role="cell" data-label={column.label}
                                            className={column.numeric ? 'pf-report-numeric' : undefined}>{column.value(row)}</td>)}
                                    </tr>
                                ))}</tbody>
                            </table>
                            <div className="pf-report-results-footer">
                                <span>{count(start + 1)}–{count(Math.min(start + pageSize, rows.length))} of {count(rows.length)} vehicles</span>
                                <div className="pf-report-results-pagination">
                                    <Select label="Rows per page" labelInline value={String(pageSize)}
                                        options={['10', '25', '50'].map(value => ({ label: value, value }))}
                                        onChange={value => { setPageSize(Number(value)); setPage(1); }} />
                                    <Pagination accessibilityLabel={`${title} pagination`}
                                        accessibilityLabels={{ previous: 'Previous results page', next: 'Next results page' }}
                                        hasPrevious={safePage > 1} hasNext={safePage < pageCount}
                                        onPrevious={() => setPage(safePage - 1)} onNext={() => setPage(safePage + 1)}
                                        label={`Page ${safePage} of ${pageCount}`} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
